import { Observable, of } from "rxjs";

export interface CacheOptions {
    duration: number;
    debug?: boolean;
}
  
export function Cache(params: CacheOptions) {
    const cache: { [k: string]: ICacheItem } = {};
    const defaultValues: Partial<CacheOptions> = {
        duration: 30000,
        debug: false
    }

    params = {
        ...defaultValues,
        ...params
    };

    return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
        let funcType: string;
        let originalFunction = descriptor?.value;

        descriptor.value = function() {
            const now = new Date();
            let cacheKeyPrefix = arguments.length > 1 ? arguments[1] : "";
            let clearCacheSetting = arguments.length > 2 && arguments[2] ? arguments[2] as CacheClear : 0;
            if (params.debug) console.log(`Clear Cache Setting: ${clearCacheSetting}`);
            let _cacheKey = `${cacheKeyPrefix}${propertyKey}${hashCode(JSON.stringify(arguments[0]))}`;
            if (params.debug) console.log(`CacheKey: ${_cacheKey}`);

            if (clearCacheSetting === CacheClear.DoNotReturn || clearCacheSetting === CacheClear.ReturnData) {
                for (let key in cache) {
                    if (key.startsWith(cacheKeyPrefix)) {
                        delete cache[key];
                    }
                }
            }

            if (clearCacheSetting === CacheClear.DoNotReturn) {
                return;
            }
            
            if (cache.hasOwnProperty(_cacheKey)) {
                let cachedResult = cache[_cacheKey];
                if (cachedResult.value && cachedResult.expiry && cachedResult.expiry > now) {
                    if (params.debug) console.log(`Retrieving Cached CacheKey: ${_cacheKey}`);
                    switch (funcType) {
                        case "observable": return of(cachedResult.value);
                        case "promise": return Promise.resolve(cachedResult.value);
                        default: return cachedResult.value;
                    }
                } else {
                    if (params.debug) console.log(`Deleting CacheKey: ${_cacheKey}`);
                    delete cache[_cacheKey];
                }
            } 
            
            const result = originalFunction.apply(this, arguments);
            cache[_cacheKey] = { value: result, expiry: new Date(now.getTime() + params.duration) } as ICacheItem;
            if (params.debug) console.log(`Setting CacheKey: ${_cacheKey}`);

            if (result instanceof Observable) {
                funcType = 'observable';
                return result;
            } else if (result instanceof Promise) {
                funcType = 'promise';
                return result
                    .then(value=> {
                        return value;
                    });
            } else {
                funcType = 'value';
                return result; 
            }
        };
    }; 
}

function hashCode (s: string) {
    return s ? s.split("").reduce(function(a: number,b: string){a=((a<<5)-a)+b.charCodeAt(0);return a&a},0).toString() : "";          
}

interface ICacheItem {
    value: any;
    expiry?: Date;
}

export enum CacheClear {
    None = 0,
    ReturnData = 1,
    DoNotReturn = 2
}